import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  HourglassSimple, ThumbsUp, ThumbsDown, CheckCircle, XCircle,
} from '@phosphor-icons/react';

export const PendingSignupsCard = ({ pendingSignups, onApprove, onReject }) => {
  if (pendingSignups.length === 0) return null;
  return (
    <Card className="border-amber-300 bg-amber-50/30" data-testid="pending-signups-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <HourglassSimple size={20} weight="duotone" className="text-amber-600" />
          Pending Agency Signups ({pendingSignups.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {pendingSignups.map((agency) => (
            <div
              key={agency.id}
              className="bg-white rounded-lg p-4 border border-amber-100 flex items-center justify-between"
              data-testid={`pending-agency-${agency.id}`}
            >
              <div className="flex-1">
                <h4 className="font-semibold text-sm text-slate-900">{agency.name}</h4>
                <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                  <span>NPI: {agency.npi}</span>
                  <span>Tax ID: {agency.tax_id}</span>
                  {agency.medicaid_provider_id && <span>Medicaid: {agency.medicaid_provider_id}</span>}
                  {agency.contact?.email && <span>{agency.contact.email}</span>}
                  {agency.address && (
                    <span>{agency.address.city}, {agency.address.state} {agency.address.zip_code}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <Button
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  onClick={() => onApprove(agency.id)}
                  data-testid={`approve-agency-${agency.id}`}
                >
                  <ThumbsUp size={14} className="mr-1" /> Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-rose-300 text-rose-600 hover:bg-rose-50"
                  onClick={() => onReject(agency.id)}
                  data-testid={`reject-agency-${agency.id}`}
                >
                  <ThumbsDown size={14} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const ViewAgencyDialog = ({ open, onOpenChange, agency, paymentMethods, getStatusBadge }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{agency?.name}</DialogTitle>
      </DialogHeader>
      {agency && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">NPI</Label>
              <p className="font-mono">{agency.npi}</p>
            </div>
            <div>
              <Label className="text-slate-500">Tax ID</Label>
              <p className="font-mono">{agency.tax_id}</p>
            </div>
            <div>
              <Label className="text-slate-500">Status</Label>
              <div className="mt-1">{getStatusBadge(agency.status)}</div>
            </div>
          </div>

          <div>
            <Label className="text-slate-500">Address</Label>
            <p>
              {agency.address?.street1}
              {agency.address?.street2 && <>, {agency.address.street2}</>}
              <br />
              {agency.address?.city}, {agency.address?.state} {agency.address?.zip_code}
              {agency.address?.county && <> ({agency.address.county} County)</>}
            </p>
          </div>

          <div>
            <Label className="text-slate-500">Contact</Label>
            <p>
              {agency.contact?.name}
              {agency.contact?.title && <> - {agency.contact.title}</>}
              <br />
              {agency.contact?.email}
              <br />
              {agency.contact?.phone}
            </p>
          </div>

          <div>
            <Label className="text-slate-500">Contracts</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {[
                { key: 'has_medicaid', label: 'Medicaid' },
                { key: 'has_embs', label: 'eMBS' },
                { key: 'has_sandata', label: 'Sandata' },
                { key: 'has_bh', label: 'BH' },
              ].map((c) => (
                agency[c.key] ? (
                  <Badge key={c.label} className="bg-green-100 text-green-700">
                    <CheckCircle size={14} className="mr-1" />{c.label}
                  </Badge>
                ) : (
                  <Badge key={c.label} variant="outline" className="text-slate-400">
                    <XCircle size={14} className="mr-1" />{c.label}
                  </Badge>
                )
              ))}
            </div>
          </div>

          {agency.payment_config && (
            <div>
              <Label className="text-slate-500">Payment Configuration</Label>
              <p>
                Method: {paymentMethods.find((m) => m.value === agency.payment_config.payment_method)?.label || agency.payment_config.payment_method}
                <br />
                Billing Email: {agency.payment_config.billing_email}
                <br />
                Auto-Pay: {agency.payment_config.auto_pay ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          )}
        </div>
      )}
    </DialogContent>
  </Dialog>
);
